import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { CheckCircle2, ShoppingBag, Loader2 } from "lucide-react";
import { toast } from "sonner";

// Shop purchase success — polls the checkout-status endpoint until Stripe
// confirms `paid`, then confirms the order + gives buyer next-steps links.
export default function ShopPurchaseSuccess() {
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ payment_status: null, order: null });
  const [attempts, setAttempts] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!sessionId) { setError("Missing session id"); return; }
    let cancelled = false;
    (async () => {
      for (let i = 0; i < 10; i++) {
        if (cancelled) return;
        try {
          const r = await api.get(`/shop/checkout/status/${sessionId}`);
          setStatus(r.data);
          setAttempts(i + 1);
          if (r.data.payment_status === "paid") return;
          if (["expired", "canceled"].includes(r.data.status)) return;
        } catch (e) {
          setError(formatApiError(e.response?.data?.detail) || e.message);
          return;
        }
        await new Promise((res) => setTimeout(res, 2000));
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  const paid = status.payment_status === "paid";
  const failed = ["expired", "canceled"].includes(status.status);

  return (
    <div className="max-w-lg mx-auto px-6 py-16 text-center" data-testid="shop-purchase-success">
      {paid ? (
        <>
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
            <CheckCircle2 size={30} className="text-emerald-700" />
          </div>
          <h1 className="mt-5 text-3xl font-black tracking-tight text-[#1B1614]">Order confirmed</h1>
          <p className="mt-3 text-[15px] text-[#5C4E48]">
            Your payment was processed successfully. The seller has been notified and will ship your item shortly.
          </p>
          {status.order && (
            <Card className="mt-6 rounded-md p-5 text-left" data-testid="success-order-card">
              <p className="text-xs uppercase tracking-wider font-mono text-[#5C4E48] mb-2">Order</p>
              <p className="font-semibold">{status.order.qty} × {status.order.product_title}</p>
              {(status.order.size || status.order.color) && (
                <p className="text-xs text-[#5C4E48] mt-1">
                  {status.order.size && `Size: ${status.order.size}`}
                  {status.order.size && status.order.color && " · "}
                  {status.order.color && `Color: ${status.order.color}`}
                </p>
              )}
              <p className="mt-3 text-lg font-black font-mono">
                ${((status.order.total_amount_cents || 0) / 100).toFixed(2)}
              </p>
            </Card>
          )}
          <div className="mt-6 flex flex-col gap-2">
            <Button onClick={() => navigate("/shop/orders")} className="rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717]" data-testid="success-view-orders">
              <ShoppingBag size={14} className="mr-1.5" /> View my orders
            </Button>
            {status.order?.business_id && (
              <Link to={`/shop/${status.order.business_id}`} className="text-sm text-[#B31E1E] hover:underline">
                Back to shop
              </Link>
            )}
          </div>
        </>
      ) : failed || error ? (
        <>
          <h1 className="text-3xl font-black text-[#B31E1E]">Checkout didn&apos;t complete</h1>
          <p className="mt-3 text-[15px] text-[#5C4E48]">{error || "Your card wasn't charged. You can try again from the shop."}</p>
          <Button onClick={() => navigate("/shops")} className="mt-6 rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717]">Back to shops</Button>
        </>
      ) : (
        <>
          <Loader2 size={30} className="mx-auto text-[#B31E1E] animate-spin" />
          <p className="mt-4 text-[#5C4E48]">Finalizing your order{attempts > 0 ? ` (${attempts}/10)` : ""}…</p>
        </>
      )}
    </div>
  );
}
